import React, { useEffect, useMemo } from "react";
import {
  Navigate,
  matchRoutes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { QueryResult } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import {
  FILESTREAM_ROUTE,
  FORBIDDEN_ROUTE,
  HELP_ROUTE,
  INSIGHTS_ROUTE,
  POLICY_CONFIG_ROUTE,
  PROJECTS_ROUTE,
  REPORTS_ROUTE,
  SOFTWARE_CATALOG_ROUTE,
  STORED_URL_DATA,
  STORE_GROUP_ID,
} from "Constants";
import { createGroupConnection, useGetWhoamiQuery } from "Services";
import { getRouteBasename } from "Utils";

const basename = getRouteBasename() ? getRouteBasename() : "/";
const reportsRoutes = [{ path: `/${REPORTS_ROUTE}/*` }];
const helpRoute = [{ path: `/${HELP_ROUTE}` }];

export function routeRequiresGroup(
  location: ReturnType<typeof useLocation>,
): boolean {
  const isFileStreamRoute = Boolean(
    matchRoutes([{ path: "/" }, { path: `/${FILESTREAM_ROUTE}/*` }], location),
  );
  const isInsightsRoute = Boolean(
    matchRoutes([{ path: `/${INSIGHTS_ROUTE}/*` }], location),
  );
  const isProjectsRoute = Boolean(
    matchRoutes([{ path: `/${PROJECTS_ROUTE}/*` }], location),
  );
  const isPolicyConfigRoute = Boolean(
    matchRoutes([{ path: `/${POLICY_CONFIG_ROUTE}/*` }], location),
  );
  const isSoftwareCatalogRoute = Boolean(
    matchRoutes([{ path: `/${SOFTWARE_CATALOG_ROUTE}/*` }], location),
  );

  return (
    isFileStreamRoute ||
    isInsightsRoute ||
    isProjectsRoute ||
    isPolicyConfigRoute ||
    isSoftwareCatalogRoute
  );
}

/**
 * User is required to belong to at least one group to be able to use main navigation tabs.
 * If a user doesn't belong to a group, they can still view pages under "Organization", but cannot use the portal.
 *
 * This component ensures a user belongs to at least one group and takes care of routing to the correct group
 *  if a user's URL does not have groupId in it.
 *
 * @param children
 * @constructor
 */
const RequireGroup = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: whoami,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetWhoamiQuery();

  const userCanViewGroup = useMemo(
    () =>
      groupId
        ? whoami?.org_groups.some((group) => group.group_id === groupId)
        : true,
    [whoami?.org_groups, groupId],
  );

  const requiresGroup = routeRequiresGroup(location);
  const isReportsRoute = Boolean(
    matchRoutes(reportsRoutes, location, basename),
  );

  useEffect(() => {
    const isHelpRoute = Boolean(matchRoutes(helpRoute, location));

    if (!whoami?.org_groups) return;

    if (groupId) {
      createGroupConnection(groupId);
    }

    if (groupId && localStorage.getItem(STORE_GROUP_ID) !== groupId) {
      STORED_URL_DATA.forEach((item) => localStorage.removeItem(item));
      localStorage.setItem(STORE_GROUP_ID, groupId);
    }

    if (requiresGroup && whoami.org_groups.length === 0) {
      navigate(`/${HELP_ROUTE}`, {
        replace: true,
      });
      return;
    }

    // redirect to stored or default group if one is not set
    if (!groupId && whoami.org_groups.length > 0) {
      const targetGroup =
        whoami.org_groups.find(
          (item) => item.group_id === localStorage.getItem(STORE_GROUP_ID),
        ) ??
        whoami.org_groups.find((group) => group.is_default) ??
        whoami.org_groups[0];

      // if a user ends up on the help page but belongs to > 0 groups, redirect to filestream
      if (isHelpRoute) {
        navigate(`/${FILESTREAM_ROUTE}/group/${targetGroup.group_id}`, {
          replace: true,
        });
      } else if (requiresGroup) {
        const pathBase = location.pathname.split("/")[1];
        navigate(
          `/${pathBase ? pathBase : FILESTREAM_ROUTE}/group/${
            targetGroup.group_id
          }`,
          {
            replace: true,
          },
        );
      }

      return;
    }
  }, [groupId, location, navigate, requiresGroup, whoami?.org_groups]);

  if (whoami?.org_groups.length === 0 && (requiresGroup || isReportsRoute)) {
    // If a user does not belong to a group, redirect them to the help page
    return <Navigate to={`/${HELP_ROUTE}`} replace={true} />;
  }

  if (!userCanViewGroup && whoami) {
    // User is trying to access a group they do not have access to
    return <Navigate to={`/${FORBIDDEN_ROUTE}`} replace={true} />;
  }

  return (
    <QueryResult
      status={getQueryStatus({
        isFetching,
        isLoading,
        isError,
        error,
        data: whoami,
      })}
    >
      {children}
    </QueryResult>
  );
};

export default RequireGroup;
